import dayjs from "dayjs";
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import { isNullOrUndefined } from "src/extensions/utils/DefineFunction";
import { defineComponent, reactive, ref, toRefs } from "vue";
import { useRouter } from "vue-router";
import { api } from "~/api";
import { GetHanyoParam } from "~/api/Common/Commands";
import { DesiredDatePrecautionsResult } from "~/api/data/DesiredDatePrecautionsResult";
import BankSelectField from "~/components/BankSelectField.vue";
import BreadcrumbList from "~/components/BreadcrumbList.vue";
import { MsgBox } from "~/components/MsgBox";
import { convertFullToHalf, Log } from "~/extensions/utils";
import { createAddress } from "~/extensions/utils/AddressHelper";
import { HanyoKb, KeyReturnNameKb, NyukyoStatus, PageID, RoomSb } from "~/extensions/utils/DefineTypes";
import { HanyoHelper } from "~/extensions/utils/HanyoHelper";
import { useLoad } from "~/pages/taikyo/kaiyaku/composition/Load";
import { commonStore } from "~/store/CommonStore";
import { myPageStore } from "~/store/MyPageStore";
import { TaikyoEntryPageViewModel } from "../shinsei/Models/TaikyoEntryPageViewModel";
import { store } from "../store/TaikyoStore";
import { KaiyakuEntryPageValidator } from "./Models/KaiyakuEntryPageValidator";
const log = Log.create("Kaiyaku.Entry");
export default defineComponent({
    name: "KaiyakuEntryPage",
    components: {
        BreadcrumbList,
        BankSelectField
    },
    props: {
        viewModel: {
            type: Object,
            required: true
        }
    },
    beforeRouteEnter(to, from, next) {
        const fromScreen = from.name;
        const relatedScreens = ["kaiyaku-guide", "kaiyaku-entry-hason", "kaiyaku-entry-check"];
        if (relatedScreens.includes(fromScreen) && store.taikyoEntity.status === NyukyoStatus.Approval) {
            next();
            return;
        }
        next({ name: "taikyo" });
    },
    async setup() {
        const router = useRouter();
        const $q = useQuasar();
        // ページ情報・基本データ
        const pageName = commonStore.pageName(PageID.kaiyakuEntry);
        const personal = reactive(myPageStore.personal);
        const nyukyokeiyaku = reactive(myPageStore.selectedNyukyokeiyaku);
        const isMultipleKeiyaku = myPageStore.isMultipleKeiyaku;
        const taikyo = reactive(store.taikyoEntity);
        const kaiyakuForinput = reactive(store.kaiyakuForInput);
        const addressAll = myPageStore.selectedNyukyokeiyaku?.addressAll;
        // HTMLコンテンツ
        const html = ref("");
        // メッセージ関連
        const desiredDateMessage = ref("");
        const desiredDateEntryMessage = ref("");
        let desiredDatePrecautions = reactive(new DesiredDatePrecautionsResult());
        // 口座情報
        const kouzaForInput = ref([]);
        // 銀行ダイアログ
        const isBankDialog = ref(false);
        // バリデーション定義
        const taikyoEntryPageViwModel = new TaikyoEntryPageViewModel(taikyo, myPageStore.selectedNyukyokeiyaku.roomSb);
        const valid = reactive(new KaiyakuEntryPageValidator(taikyoEntryPageViwModel, kaiyakuForinput));
        // 銀行選択結果を処理
        const handleBankDialogSelect = ({ bank, branch }) => {
            taikyo.henkinBankCd = bank.code;
            taikyo.henkinBansCd = branch.code;
            taikyo.henkinBankName = bank.name;
            taikyo.henkinBansName = branch.name;
        };
        // 店舗住所取得
        const getShopAdr = () => {
            if (!nyukyokeiyaku) {
                return "";
            }
            return "〒" + nyukyokeiyaku.shopPostcode + "  " + nyukyokeiyaku.shopAdr1 + nyukyokeiyaku.shopAdr2;
        };
        // 初期化処理
        const initView = async () => {
            try {
                html.value = await api.kaiyaku.loadTsuchiEntryText();
                desiredDatePrecautions = await api.taikyoNotification.loadDesiredDatePrecautionsText();
                desiredDateMessage.value = desiredDatePrecautions.precaution;
                desiredDateEntryMessage.value = desiredDatePrecautions.entryPrecaution;
                const gethanyoParam = [new GetHanyoParam(HanyoKb.KouzaSb)];
                const hanyoList = await HanyoHelper.getHanyoList(gethanyoParam);
                const publishKbnList = hanyoList.GetByHanyoKb(HanyoKb.KouzaSb);
                kouzaForInput.value = publishKbnList
                    .filter(x => [1, 2].includes(x.value))
                    .map(x => ({
                    ...x,
                    label: x.option1
                }));
                // 返金先口座情報を初期セット
                taikyo.henkinBankCd = taikyo.henkinBankCd ?? personal.bankCd;
                taikyo.henkinBankName = taikyo.henkinBankName ?? personal.bankName;
                taikyo.henkinBansCd = taikyo.henkinBansCd ?? personal.bansCd;
                taikyo.henkinBansName = taikyo.henkinBansName ?? personal.bansName;
                taikyo.henkinKouzaSb = taikyo.henkinKouzaSb ?? personal.kouzaSb;
                taikyo.henkinKouzaNo = isNullOrUndefined(taikyo.henkinKouzaNo) ? (String(personal.kouzaNo) === "0" ? "" : String(personal.kouzaNo).padStart(7, "0")) : taikyo.henkinKouzaNo;
                taikyo.henkinKouzaName = taikyo.henkinKouzaName ?? personal.kouzaName;
                taikyo.henkinKouzaKana = taikyo.henkinKouzaKana ?? personal.kouzaKana;
                // 鍵返却日時の初期セット
                if (taikyo.keyReturnYmd) {
                    taikyo.keyReturnStrYmd = dayjs(taikyo.keyReturnYmd).format("YYYY/MM/DD");
                    taikyo.keyReturnStrTime = dayjs(taikyo.keyReturnYmd).format("HH:mm");
                }
                // 解約日の初期セット
                if (taikyo.kaiyakuYmd) {
                    taikyo.kaiyakuStrYmd = dayjs(taikyo.kaiyakuYmd).format("YYYY/MM/DD");
                    if (dayjs(taikyo.kaiyakuStrYmd).isBefore(dayjs(dayjs().format("YYYY/MM/DD") + " 00:00"))) {
                        taikyo.keyReturnStrYmd = taikyo.kaiyakuStrYmd;
                        taikyo.keyReturnStrTime = "12:00";
                        taikyo.keyReturnYmd = dayjs(taikyo.kaiyakuYmd).add(12, "hour").toDate();
                    }
                }
                // 退去立会初期セット
                if (kaiyakuForinput.keyReturnNameKb === KeyReturnNameKb.HandoverDate && typeof taikyo.taikyoTachiaiName === "undefined") {
                    kaiyakuForinput.selectedTachiaishaKbn = kaiyakuForinput.tachiaishaKbnList[0];
                    taikyo.taikyoTachiaiName = personal.keiyakusha;
                }
                // 口座種別初期セット
                if (taikyo.henkinKouzaSb === 0 || typeof taikyo.henkinKouzaSb === "undefined") {
                    taikyo.henkinKouzaSb = 1;
                }
            }
            catch (ex) {
                log.error("setup", ex);
                MsgBox.error(ex?.message ?? "エラーが発生しました。");
                router.push({ name: "top" });
            }
        };
        // 郵便番号から住所検索
        const findAddress = async () => {
            if (valid.errorFields.includes("postcode")) {
                return;
            }
            if (taikyo.postCode?.length === 7) {
                taikyo.postCode = taikyo.postCode.substring(0, 3) + "-" + taikyo.postCode.substring(3);
            }
            try {
                if (/^\d{3}-\d{4}$/.test(taikyo.postCode)) {
                    taikyo.adr1 = await createAddress(taikyo.postCode);
                }
            }
            catch (ex) {
                if (ex?.status !== 404) {
                    log.error("findAddress", ex);
                }
            }
        };
        // 日時値を適用
        const applyDateTime = () => {
            if (kaiyakuForinput.keyReturnNameKb === KeyReturnNameKb.PreferredAttendanceDatetime || kaiyakuForinput.keyReturnNameKb === KeyReturnNameKb.KeyReturnScheduledDatetime) {
                const targetDatetime = dayjs(taikyo.keyReturnStrYmd + " " + taikyo.keyReturnStrTime);
                taikyo.keyReturnYmd = targetDatetime.toDate();
            }
            else if (kaiyakuForinput.keyReturnNameKb === KeyReturnNameKb.KeyEnvelopePostingDate ||
                kaiyakuForinput.keyReturnNameKb === KeyReturnNameKb.HandoverDate ||
                kaiyakuForinput.keyReturnNameKb === KeyReturnNameKb.TaikyoScheduledDate) {
                taikyo.keyReturnYmd = new Date(taikyo.keyReturnStrYmd);
            }
            else {
                taikyo.keyReturnYmd = null;
            }
        };
        // 申込処理
        const apply = () => {
            if (valid.error) {
                MsgBox.error("入力に不備があります。<br/>確認してください。");
                return;
            }
            if (taikyo.postCode?.length === 7) {
                taikyo.postCode = taikyo.postCode.substring(0, 3) + "-" + taikyo.postCode.substring(3);
            }
            taikyo.keyReturnNameKb = kaiyakuForinput.keyReturnNameKb;
            kaiyakuForinput.appliedInput = true;
            router.push({ name: taikyo.needTachiai || kaiyakuForinput.keyReturnNameKb === 4 ? "kaiyaku-entry-check" : "kaiyaku-entry-hason" });
        };
        await initView();
        return {
            ...toRefs(store),
            ...useLoad(),
            // ページ情報
            pageName,
            PageID,
            router,
            // ユーザー情報
            personal,
            nyukyokeiyaku,
            isMultipleKeiyaku,
            addressAll,
            // 契約情報
            taikyo,
            kaiyakuForinput,
            // HTML・メッセージ
            html,
            desiredDateMessage,
            desiredDateEntryMessage,
            desiredDatePrecautions,
            // フォーム関連
            kouzaForInput,
            // ダイアログ
            isBankDialog,
            // ハンドラー
            handleBankDialogSelect,
            getShopAdr,
            // バリデーション
            valid,
            // ユーティリティ関数
            findAddress,
            applyDateTime,
            apply,
            dayjs,
            convertFullToHalf,
            myPageStore,
            RoomSb,
            KeyReturnNameKb,
            $q
        };
    }
});
